import React, { useState } from "react";
import { TbEye, TbEyeOff } from "react-icons/tb";
import { api } from "../../lib/api";
import { useUiVersion } from "../../../UiVersionContext";
import { useTheme } from "../../../ThemeContext";
import { useMfa } from "../../hooks/useMfa";
import { downloadRecoveryCodes } from "../../../utils/downloadRecoveryCodes";
import { Button, Field, Alert, SettingRow, Toggle, Segmented } from "../../components";
import PasskeysSection from "./PasskeysSection";
import "./Settings.css";

// Profile, laid out as screen 14 of the design kit: identity, then the two-factor
// choice with authenticator enrolment inline, then the password change, then one
// pair of form actions at the foot.
//
// The enrolment is deliberately on this page rather than only behind a separate
// Two-factor screen: the app forces enrolment, so the method radio can't be a dead
// end. The endpoint order is unchanged — the account is not protected until
// verify-setup succeeds, so nothing here treats setup alone as enrolled.
//
// The name field is display-only: the endpoint behind this screen is a password-change
// route that accepts email, password and oldPassword and nothing else.
const ProfileScreen = ({ me }) => {
  const { mode, setMode, systemDark } = useTheme();
  const { uiVersion, setUiVersion, isUrlOverridden } = useUiVersion();
  const {
    status, error: mfaError, busy: mfaBusy, enrolment, recoveryCodes,
    beginSetup, verifySetup, setMethod,
  } = useMfa();

  const [email, setEmail] = useState(me?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [uiError, setUiError] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const changingPassword = password.length > 0 || confirm.length > 0;
  const mismatch = changingPassword && password !== confirm;
  const dirty = changingPassword || oldPassword.length > 0 || email !== (me?.email || "");

  const enrolled = !!status?.totp_enabled;
  const method = enrolled && status?.method === "totp" ? "totp" : "email";

  const discard = () => {
    setEmail(me?.email || "");
    setOldPassword(""); setPassword(""); setConfirm("");
    setError(""); setSaved("");
  };

  const submit = async () => {
    if (mismatch) { setError("The two passwords do not match."); return; }
    if (changingPassword && !oldPassword) { setError("Enter your current password to change it."); return; }
    setBusy(true); setError(""); setSaved("");
    try {
      await api.put("s1/api/update", { email, password, oldPassword });
      setSaved("Profile updated.");
      setOldPassword(""); setPassword(""); setConfirm("");
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setError(e.message || "Could not update your profile.");
    } finally { setBusy(false); }
  };

  // Choosing the authenticator when none is enrolled starts enrolment rather than
  // switching a method that does not exist yet.
  const chooseTotp = () => { if (enrolled) setMethod("totp"); else if (!enrolment) beginSetup(); };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText((recoveryCodes || []).join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable; the download button still works */ }
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Profile</h2>
          <p>Your sign-in details and how your sign-ins are verified.</p>
        </div>
      </div>

      <div className="v2-settings-form">
        <Alert>{error}</Alert>
        {saved && <Alert tone="success">{saved}</Alert>}

        <div className="v2-grid2">
          <Field label="Name">
            <input className="v2-input" value={me?.name || ""} disabled readOnly
                   title="Ask an administrator to change this." />
          </Field>
          <Field label="Email address">
            <input className="v2-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>

        <div className="v2-settings-group">
          <div className="v2-section-title">Two-factor authentication</div>
          <Alert>{mfaError}</Alert>

          <div className="v2-radio-list">
            <button
              type="button"
              className={`v2-radio-row${method === "email" ? " on" : ""}`}
              aria-pressed={method === "email"}
              disabled={mfaBusy}
              onClick={() => { if (enrolled) setMethod("email"); }}
            >
              <span className="v2-radio-dot" />
              <span>
                <span className="v2-radio-name">Emailed code</span>
                <span className="v2-radio-desc">A six-digit code sent to your address at sign-in. Valid 15 minutes.</span>
              </span>
            </button>

            <button
              type="button"
              className={`v2-radio-row${method === "totp" ? " on" : ""}`}
              aria-pressed={method === "totp"}
              disabled={mfaBusy}
              onClick={chooseTotp}
            >
              <span className="v2-radio-dot" />
              <span>
                <span className="v2-radio-name">Authenticator app</span>
                <span className="v2-radio-desc">Google Authenticator, Microsoft Authenticator, 1Password or Duo.</span>
              </span>
            </button>
          </div>

          {enrolment && (
            <div className="v2-enrol">
              {enrolment.qr && <img src={enrolment.qr} alt="Authenticator QR code" />}
              <div className="v2-enrol-body">
                <div>
                  <div className="v2-enrol-title">Scan this, then enter the code</div>
                  <div className="v2-enrol-sub">Can't scan? Enter the key by hand.</div>
                </div>
                <code className="v2-mfa-secret">{enrolment.secret}</code>
                <div className="v2-enrol-verify">
                  <input
                    className="v2-input"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                  <Button
                    variant="primary"
                    disabled={mfaBusy || code.length !== 6}
                    onClick={async () => { const ok = await verifySetup(code); if (ok) setCode(""); }}
                  >
                    {mfaBusy ? "Verifying…" : "Verify"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {recoveryCodes && recoveryCodes.length > 0 && (
            <div>
              <Alert tone="warning">
                <div>
                  <b>Save your recovery codes</b>
                  These are shown once. Without them, a lost device means a support ticket.
                </div>
              </Alert>
              <div className="v2-codes" style={{ marginTop: ".5rem" }}>
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <div className="v2-formactions" style={{ marginTop: ".625rem" }}>
                <Button onClick={() => downloadRecoveryCodes(recoveryCodes, { email })}>Download codes</Button>
                <Button variant="text" onClick={copyCodes}>{copied ? "Copied" : "Copy"}</Button>
              </div>
            </div>
          )}
        </div>

        <PasskeysSection />

        <div className="v2-settings-group">
          <div className="v2-section-title">Change password</div>
          <div className="v2-grid2">
            <Field label="Current password">
              <div className="v2-pw">
                <input className="v2-input" type={show ? "text" : "password"} value={oldPassword}
                       onChange={(e) => setOldPassword(e.target.value)} autoComplete="current-password" />
                <Button icon aria-label={show ? "Hide passwords" : "Show passwords"} onClick={() => setShow((v) => !v)}>
                  {show ? <TbEyeOff size={15} /> : <TbEye size={15} />}
                </Button>
              </div>
            </Field>
            <div />
            <Field label="New password">
              <input className="v2-input" type={show ? "text" : "password"} value={password}
                     onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </Field>
            <Field label="Confirm new password" error={mismatch ? "The two passwords do not match." : ""}>
              <input className="v2-input" type={show ? "text" : "password"} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </Field>
          </div>
        </div>

        <div className="v2-formactions">
          <Button variant="primary" onClick={submit} disabled={busy || mismatch}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
          <Button variant="text" onClick={discard} disabled={busy || !dirty}>Discard</Button>
        </div>

        <div className="v2-settings-group">
          <div className="v2-section-title">Interface</div>

          {/* Three states rather than a switch: "follow the system" is a real choice, not
              the absence of one, and a two-state toggle cannot say it. Applies at once and
              is remembered on this device — it is a display preference, not account data,
              so it is not saved to the server. */}
          <SettingRow
            name="Appearance"
            desc={mode === "system"
              ? `Following this device — currently ${systemDark ? "dark" : "light"}`
              : `Always ${mode}`}>
            <Segmented
              ariaLabel="Appearance"
              value={mode}
              onChange={setMode}
              options={[
                { value: "light", label: "Light" },
                { value: "dark", label: "Dark" },
                { value: "system", label: "System" },
              ]}
            />
          </SettingRow>

          {isUrlOverridden && (
            <Alert tone="warning">
              This page is showing a specific interface because of the <code>?ui=</code> value in the
              address bar. Remove it to return to your saved preference.
            </Alert>
          )}
          {/* <SettingRow name="Use the new interface" desc={uiVersion === "v2" ? "Currently on" : "Currently off"}>
            <Toggle
              checked={uiVersion === "v2"}
              disabled={isUrlOverridden}
              ariaLabel="Use the new interface"
              onChange={async (next) => {
                setUiError("");
                const ok = await setUiVersion(next ? "v2" : "v1");
                if (!ok) setUiError("Could not save that. Your interface has been left as it was.");
              }}
            />
          </SettingRow> */}
          <Alert>{uiError}</Alert>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
